import React, { useEffect, useMemo, useState } from "react";
import styled from "styled-components";

import { Notification } from "@/components/ui/notification";
import { useScrollableParent } from "libs/use-scrollable-parent";

const PostFieldErrorBase = ({
  className = "",
  fields,
  errors = {},
  warnings = {},
}) => {
  const [errorElement, setErrorElement] = useState(null);

  const fieldErrors = useMemo(() => {
    return (fields || [])
      .filter((field) => errors?.[field]?.errors?.length > 0 && field)
      .flatMap((field) => errors[field].errors);
  }, [errors, fields]);

  const fieldWarnings = useMemo(() => {
    return warnings
      ? (fields || [])
          .filter((field) => warnings?.[field]?.warnings?.length > 0 && field)
          .flatMap((field) => warnings[field].warnings)
      : [];
  }, [warnings, fields]);

  const scrollableParent = useScrollableParent(errorElement);

  useEffect(() => {
    if (fieldErrors.length > 0 && errorElement && scrollableParent) {
      const errorOffset = errorElement.getBoundingClientRect().top;

      if (errorOffset < 0) {
        scrollableParent.scrollTo({
          top: Math.abs(scrollableParent.scrollTop - Math.abs(errorOffset)),
          behavior: "smooth",
        });
      }
    }
  }, [errorElement, fieldErrors, scrollableParent]);

  if (fieldErrors.length === 0 && fieldWarnings.length === 0) {
    return null;
  }

  return (
    <div
      data-testid="postErrorList"
      className={`grid grid-cols-1 gap-2.5 ${className}`}
      ref={setErrorElement}
    >
      {fieldErrors?.length > 0 && (
        <Notification
          variant="error"
          title={fieldErrors.map((err, index) => (
            <div key={`field-err-${index}`}>
              <div>{err?.error ?? err}</div>
            </div>
          ))}
        />
      )}
      {fieldWarnings?.length > 0 && (
        <Notification
          variant="warning"
          title={fieldWarnings.map((warning, index) => (
            <div key={`field-warning-${index}`}>
              <div>{warning}</div>
            </div>
          ))}
        />
      )}
    </div>
  );
};

export const PostFieldError = styled(PostFieldErrorBase)``;

// ErrorList component exported for use in other files
export const ErrorList = ({ children, className = "" }) => (
  <div className={`grid grid-cols-1 gap-2.5 ${className}`}>{children}</div>
);

export default PostFieldError;
